import { EventClickArg, EventContentArg, EventDropArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import { Box } from '@mui/material';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DashboardOrderSummary, OrderStatus } from '../../types';
import { getDashboardOrderColor, normalizeOrderStatus } from '../../utils';
import { getOrderStatusIcon } from './order-status-icons';
import { OrdersMorePopup } from './orders-more-popup';

interface DashboardCalendarProps {
  orders: DashboardOrderSummary[];
  currentDate: Date;
  viewMode: 'month' | 'week';
  showPONumbers: boolean;
  canReschedule: boolean;
  onOrderClick: (order: DashboardOrderSummary) => void;
  onEventDrop: (dropInfo: EventDropArg) => void;
  onDateChange: (date: Date) => void;
  onDateRangeChange?: (startDate: Date, endDate: Date) => void;
  isLoading: boolean;
  maxOrdersPerDay?: number; // Maximum number of orders to display per day before showing "more"
}

const DEFAULT_MAX_ORDERS_PER_DAY = 4;

// Only show orders with these statuses on the calendar
const ALLOWED_CALENDAR_STATUSES = [
  OrderStatus.CHECKED_OUT,
  OrderStatus.IN_QUEUE,
  OrderStatus.SAW,
  OrderStatus.WELD,
  OrderStatus.PAINT,
  OrderStatus.ON_HOLD,
  OrderStatus.COMPLETE,
  OrderStatus.READY_FOR_PICKUP,
  OrderStatus.SHIPPED,
];

export const DashboardCalendar = ({
  orders,
  currentDate,
  viewMode,
  showPONumbers,
  canReschedule,
  onOrderClick,
  onEventDrop,
  onDateChange,
  onDateRangeChange,
  maxOrdersPerDay = DEFAULT_MAX_ORDERS_PER_DAY,
}: DashboardCalendarProps) => {
  const calendarRef = useRef<FullCalendar>(null);
  const [morePopupOpen, setMorePopupOpen] = useState(false);
  const [morePopupDate, setMorePopupDate] = useState<string>('');
  const [morePopupOrders, setMorePopupOrders] = useState<DashboardOrderSummary[]>([]);

  // Change view when viewMode changes
  useEffect(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      const targetView = viewMode === 'week' ? 'dayGridWeek' : 'dayGridMonth';
      if (calendarApi.view.type !== targetView) {
        calendarApi.changeView(targetView);
      }
    }
  }, [viewMode]);

  // Convert orders to FullCalendar events with limit per day
  const events = useMemo(() => {
    // Group orders by date
    const ordersByDate = new Map<string, DashboardOrderSummary[]>();

    orders
      .filter(order => {
        // Filter by dueDate and allowed statuses
        if (!order.dueDate) return false;
        const normalizedStatus = normalizeOrderStatus(order.status);
        return ALLOWED_CALENDAR_STATUSES.includes(normalizedStatus);
      })
      .forEach(order => {
        const dueDate = DateTime.fromISO(order.dueDate!);
        if (!dueDate.isValid) {
          console.warn('Invalid due date for order:', order.id, order.dueDate);
          return;
        }

        const isoDate = dueDate.toISODate();
        if (!isoDate) {
          console.warn('Failed to format date for order:', order.id);
          return;
        }

        if (!ordersByDate.has(isoDate)) {
          ordersByDate.set(isoDate, []);
        }
        ordersByDate.get(isoDate)!.push(order);
      });

    // Create events with limit per day
    const calendarEvents: Array<{
      id: string;
      title: string;
      start: string;
      backgroundColor: string;
      borderColor: string;
      textColor: string;
      classNames?: string[];
      eventOrder?: number; // Top-level property for FullCalendar sorting
      extendedProps: {
        order?: DashboardOrderSummary;
        displayText?: string;
        statusIcon?: string;
        fullTitle?: string;
        isMoreEvent?: boolean;
        allOrders?: DashboardOrderSummary[];
        moreDate?: string;
      };
    }> = [];

    ordersByDate.forEach((dayOrders, dateStr) => {
      // Sort orders (you might want to add custom sorting logic here)
      const sortedOrders = [...dayOrders];

      // Split into visible and remaining
      const visibleOrders = sortedOrders.slice(0, maxOrdersPerDay);
      const remainingOrders = sortedOrders.slice(maxOrdersPerDay);

      // Create events for visible orders
      visibleOrders.forEach((order, index) => {
        // Normalize status to ensure proper icon mapping
        const normalizedStatus = normalizeOrderStatus(order.status);
        const statusIcon = getOrderStatusIcon(normalizedStatus);
        const displayText = showPONumbers ? order.po : order.orderNumber;
        const backgroundColor = getDashboardOrderColor(order.colorCode);
        const fullTitle = `${displayText} ${statusIcon}`;

        calendarEvents.push({
          id: order.id.toString(),
          title: fullTitle,
          start: dateStr,
          backgroundColor,
          borderColor: backgroundColor,
          textColor: '#FFFFFF',
          eventOrder: index, // Lower order for regular events
          extendedProps: {
            order,
            displayText,
            statusIcon,
            fullTitle,
          },
        });
      });

      // Create "more" event if there are remaining orders
      // Use a high eventOrder value to ensure it appears last
      if (remainingOrders.length > 0) {
        calendarEvents.push({
          id: `more-${dateStr}`,
          title: `${remainingOrders.length} more`,
          start: dateStr,
          backgroundColor: '#757575', // Gray for "more" indicator
          borderColor: '#757575',
          textColor: '#FFFFFF',
          classNames: ['fc-more-event'], // Custom class for CSS targeting
          eventOrder: 9999, // Very high order value to appear last
          extendedProps: {
            isMoreEvent: true,
            allOrders: sortedOrders, // Store ALL orders for the popup
            moreDate: dateStr,
          },
        });
      }
    });

    return calendarEvents;
  }, [orders, showPONumbers, maxOrdersPerDay]);

  const handleEventClick = useCallback(
    (clickInfo: EventClickArg) => {
      const { isMoreEvent, allOrders, moreDate, order } = clickInfo.event.extendedProps as {
        isMoreEvent?: boolean;
        allOrders?: DashboardOrderSummary[];
        moreDate?: string;
        order?: DashboardOrderSummary;
      };

      if (isMoreEvent && allOrders && moreDate) {
        // Open popup with ALL orders for that date
        setMorePopupDate(moreDate);
        setMorePopupOrders(allOrders);
        setMorePopupOpen(true);
      } else if (order) {
        // Open order detail popup
        onOrderClick(order);
      }
    },
    [onOrderClick]
  );

  const handleMorePopupClose = useCallback(() => {
    setMorePopupOpen(false);
    setMorePopupDate('');
    setMorePopupOrders([]);
  }, []);

  const handleEventDrop = useCallback(
    (dropInfo: EventDropArg) => {
      onEventDrop(dropInfo);
    },
    [onEventDrop]
  );

  const handleDatesSet = useCallback(
    (arg: { start: Date; end: Date; startStr: string; endStr: string; view: { type: string } }) => {
      // For month view, use the first day of the month being displayed
      // FullCalendar's start date might be from the previous month if the month starts mid-week
      // So we calculate the first day of the month from the middle of the visible range
      if (viewMode === 'month') {
        const midRange = new Date((arg.start.getTime() + arg.end.getTime()) / 2);
        const firstDayOfMonth = new Date(midRange.getFullYear(), midRange.getMonth(), 1);
        onDateChange(firstDayOfMonth);
      } else {
        // For week view, use the start date directly
        onDateChange(arg.start);
        // Pass the visible date range to parent for API requests
        // FullCalendar's end date is exclusive, so we subtract 1 day to get the inclusive end date
        const inclusiveEndDate = new Date(arg.end);
        inclusiveEndDate.setDate(inclusiveEndDate.getDate() - 1);
        onDateRangeChange?.(arg.start, inclusiveEndDate);
      }
    },
    [onDateChange, onDateRangeChange, viewMode]
  );

  // Custom event content renderer with truncation and tooltip
  const renderEventContent = useCallback((eventInfo: EventContentArg) => {
    const { displayText, statusIcon, fullTitle, isMoreEvent } = eventInfo.event.extendedProps as {
      displayText?: string;
      statusIcon?: string;
      fullTitle?: string;
      isMoreEvent?: boolean;
    };

    // Create a wrapper div with title attribute for tooltip
    const wrapper = document.createElement('div');
    wrapper.className = 'fc-event-title-container';

    if (isMoreEvent) {
      // For "more" events, show the count
      wrapper.setAttribute('title', `Click to see ${eventInfo.event.title} orders`);
      wrapper.style.fontWeight = '600';
      wrapper.style.cursor = 'pointer';
      const content = document.createTextNode(eventInfo.event.title);
      wrapper.appendChild(content);
    } else {
      // For regular order events
      wrapper.setAttribute('title', fullTitle || ''); // Native browser tooltip
      const content = document.createTextNode(`${displayText || ''} ${statusIcon || ''}`);
      wrapper.appendChild(content);
    }

    return { domNodes: [wrapper] };
  }, []);

  // Build calendar props - use proper typing instead of Record
  const calendarProps = {
    plugins: [dayGridPlugin, interactionPlugin],
    initialView: (viewMode === 'week' ? 'dayGridWeek' : 'dayGridMonth') as
      | 'dayGridWeek'
      | 'dayGridMonth',
    initialDate: currentDate,
    events: events,
    editable: canReschedule,
    droppable: canReschedule,
    eventClick: handleEventClick,
    eventContent: renderEventContent,
    datesSet: handleDatesSet,
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: '',
    },
    buttonText: {
      today: 'Today',
    },
    height: 'auto' as const,
    dayMaxEvents: false as const, // We handle limiting manually
    eventDisplay: 'block' as const,
    eventOrder: 'eventOrder', // Use top-level eventOrder property for sorting
    weekends: true,
    ...(canReschedule && { eventDrop: handleEventDrop }),
  };

  return (
    <Box
      sx={{
        '& .fc-event-title-container': {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '100%',
          maxWidth: '100%',
        },
        '& .fc-event-title': {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
        // Make day cells square and larger
        '& .fc-daygrid-day': {
          minHeight: '140px',
          position: 'relative',
        },
        '& .fc-daygrid-day-frame': {
          minHeight: '140px',
          position: 'relative',
        },
        // Use aspect-ratio to ensure square cells in month view
        '& .fc-dayGridMonth-view .fc-daygrid-day': {
          aspectRatio: '1',
          minHeight: '140px',
        },
        '& .fc-dayGridWeek-view .fc-daygrid-day': {
          minHeight: '140px',
        },
        // Ensure "more" events appear at the bottom of the day
        '& .fc-daygrid-day-events': {
          display: 'flex',
          flexDirection: 'column',
        },
        '& .fc-event.fc-more-event': {
          order: 999, // Force to appear last
          marginTop: 'auto', // Push to bottom
        },
      }}
    >
      <FullCalendar ref={calendarRef} {...calendarProps} />
      <OrdersMorePopup
        open={morePopupOpen}
        date={morePopupDate}
        orders={morePopupOrders}
        showPONumbers={showPONumbers}
        onClose={handleMorePopupClose}
        onOrderClick={onOrderClick}
      />
    </Box>
  );
};
